import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuthStore } from '../stores/authStore';
import { supabase } from '../lib/supabase';
import { Persona } from '../lib/types';
import posthog from '../lib/posthog';
import { useRealtimeSync } from '../lib/hooks/useRealtimeSync';

interface PersonaContextType {
  personas: Persona[];
  fetchPersonas: () => Promise<void>;
  setPersonas: React.Dispatch<React.SetStateAction<Persona[]>>;
  createPersona: (
    persona: Omit<
      Persona,
      'id' | 'created_at' | 'updated_at' | 'user_id' | 'is_example'
    >,
  ) => Promise<Persona>;
  updatePersona: (
    id: string,
    persona: Partial<Persona>,
  ) => Promise<Persona | null>;
  deletePersona: (id: string) => Promise<void>;
  loading: boolean;
  error: string | null;
}

const PersonaContext = createContext<PersonaContextType | undefined>(undefined);

export function PersonaProvider({ children }: { children: React.ReactNode }) {
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuthStore();

  const workspaceId = user?.currentWorkspace?.workspace_id;

  const fetchPersonas = async () => {
    try {
      if (!user) {
        setPersonas([]);
        setError(null);
        setLoading(false);
        return;
      }

      if (!workspaceId) {
        if (user.workspace_profiles == null) {
          setError(null);
          setLoading(true);
          return;
        }

        throw new Error('Workspace ID missing from user');
      }

      setLoading(true);
      const { data, error } = await supabase
        .from('personas_public')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('name'); // Order by name alphabetically

      if (error) throw error;
      setError(null);
      setPersonas(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch personas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPersonas();
  }, [user, workspaceId]);

  useRealtimeSync('personas', workspaceId, fetchPersonas);

  const createPersona = async (
    personaData: Omit<
      Persona,
      'id' | 'created_at' | 'updated_at' | 'user_id' | 'is_example'
    >,
  ) => {
    try {
      if (!user) throw new Error('User not found');
      const workspaceId = user?.currentWorkspace?.workspace_id;
      if (!workspaceId) throw new Error('Workspace ID missing from user');
      const { data, error } = await supabase
        .from('personas')
        .insert([
          {
            ...personaData,
            is_example: false,
            user_id: user?.id,
            workspace_id: workspaceId,
          },
        ])
        .select()
        .single();

      if (error) throw error;

      posthog.capture('persona_created', {
        persona_id: data.id,
        workspace_id: workspaceId,
      });

      // Insert the new persona in the correct alphabetical position
      setPersonas((current) => {
        const newList = [...current, data];
        return newList.sort((a, b) => a.name.localeCompare(b.name));
      });

      return data;
    } catch (err) {
      posthog.captureException(err);
      throw new Error(
        err instanceof Error ? err.message : 'Failed to create persona',
      );
    }
  };

  const updatePersona = async (
    id: string,
    personaData: Partial<Persona>,
  ): Promise<Persona | null> => {
    try {
      if (!user) throw new Error('User not found');
      const workspaceId = user?.currentWorkspace?.workspace_id;
      if (!workspaceId) throw new Error('Workspace ID missing from user');
      const { data, error } = await supabase
        .from('personas')
        .update(personaData)
        .eq('id', id)
        .eq('workspace_id', workspaceId)
        .select()
        .single();

      if (error) throw error;

      posthog.capture('persona_updated', {
        persona_id: id,
        workspace_id: workspaceId,
      });

      // Update the persona and maintain alphabetical order
      setPersonas((current) => {
        const newList = current.map((persona) =>
          persona.id === id ? { ...persona, ...data } : persona,
        );
        return newList.sort((a, b) => a.name.localeCompare(b.name));
      });

      return data;
    } catch (err) {
      posthog.captureException(err);
      throw new Error(
        err instanceof Error ? err.message : 'Failed to update persona',
      );
    }
  };

  const deletePersona = async (id: string) => {
    try {
      if (!user) throw new Error('User not found');
      const workspaceId = user?.currentWorkspace?.workspace_id;
      if (!workspaceId) throw new Error('Workspace ID missing from user');
      const { error } = await supabase
        .from('personas')
        .delete()
        .eq('id', id)
        .eq('workspace_id', workspaceId);

      if (error) throw error;

      posthog.capture('persona_deleted', {
        persona_id: id,
        workspace_id: workspaceId,
      });

      setPersonas((current) => current.filter((persona) => persona.id !== id));
    } catch (err) {
      posthog.captureException(err);
      throw new Error(
        err instanceof Error ? err.message : 'Failed to delete persona',
      );
    }
  };

  return (
    <PersonaContext.Provider
      value={{
        personas,
        setPersonas,
        fetchPersonas,
        createPersona,
        updatePersona,
        deletePersona,
        loading,
        error,
      }}
    >
      {children}
    </PersonaContext.Provider>
  );
}

export function usePersonas() {
  const context = useContext(PersonaContext);
  if (context === undefined) {
    throw new Error('usePersonas must be used within a PersonaProvider');
  }
  return context;
}
